// Live feed for the hosted overlay page — HYBRID by design:
//   • Twitch channels connect straight from the browser (anonymous IRC), so a
//     Twitch overlay needs no server at all — it scales on the edge and keeps
//     working even if the homelab is down. This is the common case.
//   • Kick / YouTube / TikTok channels (which a browser can't reach) come over a
//     WebSocket from the homelab ingestion service.
// Both sources merge into one stream and render through the SAME OverlayChat the
// in-app builder uses (synced). Cosmetics (7TV paint/badge + third-party) resolve
// per chatter via the identity API and are asked again whenever they change (see
// COSMETICS_TTL_MS); moderation deletes retract messages.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { OverlayChat } from './OverlayChat';
import { connectTwitch, type OverlayCommand } from './twitchClient';
import { badgeRoomsFor, lookupTwitchBadge, storeRoomBadges, type TwitchBadgeRooms } from './twitchBadgeRooms';
import {
  badgeWithBackdrop,
  clampOverlayStyle,
  DEFAULT_OVERLAY_STYLE,
  FFZ_MOD_BACKDROP,
  ffzRoomRoleArt,
  type OverlayStyle,
  type SeventvEmoteChange,
} from './overlayConfig';
import type { OverlayAtmosphere, OverlayBaseMessage, OverlayMessage, OverlayPaint } from './overlayMessage';
import { forgetIdentityPersonalSet, installPersonalSet, onSevenTvCosmeticChange, type PersonalSetAnswer } from './seventvLive';
import { watchMemberCosmetics } from './memberCosmeticsLive';
import { startEmotePrefill } from './emotePrefill';
import { createRelayRows } from './relayRows';
import type { SegmentUpdate } from './emoteRepair';
import { lookFor, lookStandIn, rememberLook } from './wornStore';
import { aliveKey, HEARTBEAT_MS, shouldRestore } from './reloadRestore';
import { channelRowKey, goneChannels, isFromGoneChannel } from './channelRows';
import { newPaceState, noteShown, showAt, type PaceState } from './paceRelease';
import { noteFrame, sinceParam } from './relayCursor';

const OVERLAY_WS = import.meta.env.VITE_OVERLAY_WS || 'wss://overlay.streamnook.app/ws';
const BUFFER_MAX = 200;
// Persisted-buffer cap: enough to refill the canvas with the chats that were on
// screen, without bloating localStorage.
const RESTORE_MAX = 40;
// How long a message waits for its author's cosmetics before showing anyway, so a
// slow/failed resolve never holds chat indefinitely.
// Measured 2026-10-21: an identity lookup takes 0.6 to 1.3 s (7TV is most of
// it), so 1 s let many rows show plain and then decorate.
const MAX_HOLD_MS = 1500;
// The first ask for new chatters goes out this soon (a burst still shares one
// request); asks prompted by a change event keep the longer settle below.
const FIRST_ASK_MS = 40;
const REFRESH_STAMP_KEY = 'sn_overlay_last_refresh';
// A changed cosmetic reaches the overlay the moment it changes: StreamNook's own
// (badge, member number, Atmosphere, worn chat badges) through realtime on its
// tables (memberCosmeticsLive), 7TV paints and badges through the channel's 7TV
// EventAPI entitlements (seventvLive). Either one asks for just that chatter
// again. The safety net for anything no event covers (a 7TV user whose client
// sent no presence, a chat badge provider's own list): a chatter's cosmetics are
// asked again on their next message once this old.
const COSMETICS_TTL_MS = 60_000;
// 7TV announces a change as it saves it; its API is asked a beat later so the
// answer already carries the new paint or badge.
const SEVENTV_SETTLE_MS = 1_000;
const REFRESH_COOLDOWN_MS = 10_000;

export interface ChannelRef {
  provider: string;
  channel: string;
}

// Matches the server's OverlayIdentity — the full cosmetic stack per chatter.
interface ResolvedIdentity {
  twitch_user_id: string;
  paint: unknown | null;
  seventv_badge: { url: string; title: string } | null;
  badges: { title: string; image_url: string; source: string; category?: string; backdrop?: string }[];
  sn_number: number | null;
  sn_badge_url: string | null;
  atmosphere: OverlayAtmosphere | null;
  /** 7TV could not answer: paint and seventv_badge are unknown, not absent. */
  seventv_unavailable?: true;
  /** A 7TV subscriber's personal emotes (Twitch chatters only). */
  seventv_personal?: PersonalSetAnswer;
}

/** A bare Twitch user id: the only key 7TV keys personal emotes by. */
const TWITCH_KEY = /^\d+$/;

/** Put a chatter's personal emotes in place before their held rows show, or
 *  take back an earlier answer's when this one has none. */
function installPersonal(key: string, identity: ResolvedIdentity) {
  if (!TWITCH_KEY.test(key)) return;
  if (identity.seventv_personal) installPersonalSet(key, identity.seventv_personal);
  else forgetIdentityPersonalSet(key);
}

/** A channel's FrankerFaceZ moderator and VIP art, finished the way the app
 *  draws it (the moderator glyph on its green square). Null for a role the
 *  room does not replace. */
interface RoleArt {
  moderator: string | null;
  vip: string | null;
}

// One read per room for the page's life; a failed read is tried again later.
const roleArtCache = new Map<string, Promise<RoleArt | null>>();

/** A channel's FFZ moderator and VIP art from FFZ's public room API (no
 *  sign-in), the same data the app reads. Null when the read failed. */
function loadRoleArt(roomId: string): Promise<RoleArt | null> {
  const cached = roleArtCache.get(roomId);
  if (cached) return cached;
  const read = (async (): Promise<RoleArt | null> => {
    const res = await fetch(`https://api.frankerfacez.com/v1/room/id/${encodeURIComponent(roomId)}`);
    if (res.status === 404) return { moderator: null, vip: null };
    if (!res.ok) throw new Error(`ffz room ${res.status}`);
    const urls = ffzRoomRoleArt(await res.json());
    let moderator: string | null = null;
    if (urls.moderator) {
      const png = await fetch(urls.moderator).then((r) => (r.ok ? r.arrayBuffer() : null));
      moderator = png ? badgeWithBackdrop(new Uint8Array(png), FFZ_MOD_BACKDROP) : null;
    }
    return { moderator, vip: urls.vip };
  })().catch(() => {
    roleArtCache.delete(roomId);
    return null;
  });
  roleArtCache.set(roomId, read);
  return read;
}

function emptyIdentity(id: string): ResolvedIdentity {
  return { twitch_user_id: id, paint: null, seventv_badge: null, badges: [], sn_number: null, sn_badge_url: null, atmosphere: null };
}

/**
 * The key a chatter's cosmetics are resolved and cached under, in their own
 * platform's id space. Kick ids are small integers from the same range as
 * Twitch ids, so a bare Kick id read as a Twitch id is a Twitch stranger: their
 * paint, badges and membership. Twitch keys stay bare; Kick and YouTube are
 * namespaced, which is what the resolve endpoint expects. TikTok has no key:
 * 7TV has no TikTok platform and no member can claim a TikTok account.
 */
function cosmeticsKeyOf(m: OverlayBaseMessage): string | undefined {
  if (!m.user_id) return undefined;
  const provider = m.provider ?? 'twitch';
  if (provider === 'twitch') return m.user_id;
  if (provider === 'kick' || provider === 'youtube') return `${provider}:${m.user_id}`;
  return undefined;
}

function adaptMessage(
  m: OverlayBaseMessage,
  cos: Map<string, ResolvedIdentity>,
  twBadges: TwitchBadgeRooms,
  roleArt: Map<string, RoleArt> | null,
): OverlayMessage {
  if (m.seventvEmoteChange) return m;
  const key = cosmeticsKeyOf(m);
  const r = key ? cos.get(key) : undefined;
  const paint = (r?.paint as OverlayPaint | null | undefined) ?? undefined;
  // Resolve Twitch badge NAME/VERSION → image + title (the feed carries only
  // name/version). The title matters for detection — e.g. the "Chat Bot" badge
  // (set 'bot-badge') flags bot accounts, so bot filtering needs it.
  // YouTube rows keep their own badges (real member art in image_url_1x; the
  // renderer supplies YouTube role art). Only non-YouTube providers resolve
  // name/version through the Twitch badge map, so Twitch badges are untouched.
  // The channel's own FFZ moderator and VIP art, only while the overlay asks
  // for it (`roleArt` is null otherwise): Twitch's art stays in place.
  const roomArt = (m.provider ?? 'twitch') === 'twitch' && roleArt ? roleArt.get(m.tags?.['room-id'] ?? '') : undefined;
  const badges =
    m.provider === 'youtube'
      ? m.badges
      : m.badges.map((b) => {
          const custom = roomArt ? (b.name === 'moderator' ? roomArt.moderator : b.name === 'vip' ? roomArt.vip : null) : null;
          // Looked up in the set of the channel the badge was earned in, so a
          // channel's own subscriber art never gives way to another's.
          const info = b.image_url_4x && b.title ? null : lookupTwitchBadge(twBadges, m.tags, `${b.name}/${b.version}`);
          const resolved = info ? { ...b, image_url_4x: b.image_url_4x ?? info.url, title: b.title ?? info.title } : b;
          return custom ? { ...resolved, image_url_4x: custom } : resolved;
        });
  return {
    ...m,
    badges,
    paint,
    seventvBadgeUrl: r?.seventv_badge?.url,
    seventvBadgeTitle: r?.seventv_badge?.title,
    // What the app's chat draws for this chatter (the identity API applies its
    // rules, BetterTTV Pro included: only a member who chose it wears it).
    extraBadges: (r?.badges ?? []).map((b) => ({
      url: b.image_url,
      title: b.title,
      source: b.source,
      category: b.category,
      ...(b.backdrop ? { backdrop: b.backdrop } : {}),
    })),
    streamNookUserNumber: r?.sn_number ?? undefined,
    streamNookBadgeUrl: r?.sn_badge_url ?? undefined,
    atmosphere: r?.atmosphere ?? undefined,
  };
}

export function HostedOverlayFeed({
  overlayId,
  channels,
  style: rawStyle,
  relayQuery,
  showStatus = false,
  persist = true,
}: {
  overlayId: string;
  channels: ChannelRef[];
  style: Record<string, unknown>;
  /** How the relay socket is opened, asked again on every connect, or null to
   *  wait and retry. Absent: by this overlay's published id (the OBS page). */
  relayQuery?: () => Promise<string | null>;
  /** Show a "Waiting for chat…" line while nothing has arrived (the builder
   *  preview). Never on the OBS page: there it would be drawn on stream, at
   *  every quiet start and after every clear. */
  showStatus?: boolean;
  /** Keep the last chats across a reload, saved under the overlay's id (the OBS
   *  page). The builder preview passes false: it is not what is on stream, so
   *  it must neither bring back old rows on a refresh nor write the buffer the
   *  published overlay restores from. */
  persist?: boolean;
}) {
  const style: OverlayStyle = useMemo(
    () => clampOverlayStyle({ ...DEFAULT_OVERLAY_STYLE, ...rawStyle } as OverlayStyle),
    [rawStyle],
  );

  const [messages, setMessages] = useState<OverlayBaseMessage[]>([]);
  const [cosmeticsVersion, setCosmeticsVersion] = useState(0);
  const cosmeticsRef = useRef<Map<string, ResolvedIdentity>>(new Map());
  // When each chatter's cosmetics were last resolved (see COSMETICS_TTL_MS).
  const resolvedAtRef = useRef<Map<string, number>>(new Map());
  // The newest ask per chatter. Asks can overlap (a change event while an
  // earlier ask is still out) and only the newest one's answer is kept, so an
  // answer from before a change never lands after the one from after it.
  const askSeqRef = useRef(0);
  const latestAskRef = useRef<Map<string, number>>(new Map());
  // Each channel's FFZ moderator and VIP art by room id, read while the
  // overlay shows custom role art.
  const customRoleArt = style.ffzCustomRoleBadges === true;
  const customRoleArtRef = useRef(customRoleArt);
  const roleArtRef = useRef<Map<string, RoleArt>>(new Map());
  const pendingRef = useRef<Set<string>>(new Set());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Twitch badge NAME/VERSION → { image, title }, loaded once per channel and
  // kept per room id (see twitchBadgeRooms).
  const twitchBadgesRef = useRef<TwitchBadgeRooms>(new Map());
  const loadedRoomsRef = useRef<Set<string>>(new Set());
  // Hold-until-decorated queue: an incoming message waits here until its author's
  // cosmetics are resolved (or MAX_HOLD_MS elapses), then releases — so a message
  // never appears plain and then pops its paint/badges in a moment later.
  const holdQueueRef = useRef<{ msg: OverlayBaseMessage; ts: number }[]>([]);
  // Replay guard: an upstream reconnect can re-send recent history (YouTube's
  // first poll after a re-resolve returns a snapshot of the last messages), and
  // the localStorage restore replays the saved buffer. Same ids either way, so
  // showing them again would flood the overlay with messages it already showed.
  const seenIdsRef = useRef<Set<string>>(new Set());

  // Rows leave the queue one at a time at the rhythm they arrived (paceRelease),
  // not all at once when a lookup answers; the timer wakes the queue exactly
  // when its next row is due.
  const paceRef = useRef<PaceState>(newPaceState());
  const drainTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const drain = useCallback(() => {
    if (drainTimerRef.current) clearTimeout(drainTimerRef.current);
    drainTimerRef.current = null;
    const now = Date.now();
    const q = holdQueueRef.current;
    const release: OverlayBaseMessage[] = [];
    let wakeAt = Infinity;
    while (q.length > 0) {
      const head = q[0];
      // Ready once the author's cosmetics are cached (resolved — even to "none") or
      // the hold times out. Stop at the first not-ready message so order is kept:
      // a later message never jumps ahead of an earlier, still-holding one.
      const key = cosmeticsKeyOf(head.msg);
      const ready = !key || cosmeticsRef.current.has(key) || now - head.ts >= MAX_HOLD_MS;
      if (!ready) {
        wakeAt = head.ts + MAX_HOLD_MS;
        break;
      }
      const catchUp = !!head.msg.metadata?.from_backfill;
      const at = showAt(paceRef.current, head.ts, catchUp);
      if (at > now) {
        wakeAt = at;
        break;
      }
      q.shift();
      release.push(head.msg);
      if (!catchUp) noteShown(paceRef.current, head.ts, now);
    }
    if (release.length > 0) {
      setMessages((prev) => {
        const next = [...prev, ...release];
        return next.length > BUFFER_MAX ? next.slice(next.length - BUFFER_MAX) : next;
      });
    }
    // The timer fires after drain exists, so naming itself here is safe.
    // eslint-disable-next-line react-hooks/immutability
    if (wakeAt !== Infinity) drainTimerRef.current = setTimeout(drain, Math.max(0, wakeAt - now));
  }, []);

  // Resolve chatters' cosmetics, batched (debounced 250ms).
  const flush = useCallback(async () => {
    flushTimerRef.current = null;
    // Cap per-call so a raid doesn't fire hundreds of 7TV lookups at once (stays
    // under edge subrequest limits + friendly to 7TV). Remaining users resolve on
    // the next tick.
    const ids = Array.from(pendingRef.current).slice(0, 25);
    for (const id of ids) pendingRef.current.delete(id);
    if (ids.length === 0) return;
    const ask = ++askSeqRef.current;
    for (const id of ids) latestAskRef.current.set(id, ask);
    // A chatter who could not be looked up and is not drawn yet wears the look
    // they had last (wornStore), unstamped, so their next message asks again.
    const standIn = (id: string) => {
      if (latestAskRef.current.get(id) !== ask) return;
      const look = lookStandIn(id, cosmeticsRef.current.has(id));
      if (look) cosmeticsRef.current.set(id, { ...emptyIdentity(id), ...look } as ResolvedIdentity);
    };
    try {
      // Never from a cache: an ask made because something changed has to see
      // the change.
      const res = await fetch(`/api/v1/identity?resolve=1&ids=${ids.map(encodeURIComponent).join(',')}`, {
        cache: 'no-store',
      });
      if (res.ok) {
        const data = (await res.json()) as { identities?: Record<string, ResolvedIdentity>; unresolved?: string[] };
        // A key the server could not look up stays uncached, so the chatter's
        // next message asks again instead of showing no cosmetics all stream.
        const unresolved = new Set(data.unresolved ?? []);
        const now = Date.now();
        for (const id of ids) {
          if (unresolved.has(id)) standIn(id);
          if (unresolved.has(id) || latestAskRef.current.get(id) !== ask) continue;
          const fresh = data.identities?.[id] ?? emptyIdentity(id);
          if (fresh.seventv_unavailable) {
            // 7TV did not answer: keep the paint and 7TV badge already shown
            // (a lookup that failed must never strip them), or the ones this
            // chatter wore last time, take the rest, and leave it unstamped so
            // the chatter's next message asks again.
            const prev = cosmeticsRef.current.get(id);
            const last = prev ? null : (lookFor(id) as Partial<ResolvedIdentity> | null);
            cosmeticsRef.current.set(id, {
              ...fresh,
              paint: prev ? prev.paint : (last?.paint ?? null),
              seventv_badge: prev ? prev.seventv_badge : (last?.seventv_badge ?? null),
            });
            resolvedAtRef.current.delete(id);
            continue;
          }
          cosmeticsRef.current.set(id, fresh);
          resolvedAtRef.current.set(id, now);
          installPersonal(id, fresh);
          // A real answer: shown at once next session, and while 7TV is down.
          rememberLook(id, fresh);
        }
      } else {
        for (const id of ids) standIn(id);
      }
    } catch {
      /* re-queued on the next message */
      for (const id of ids) standIn(id);
    }
    setCosmeticsVersion((v) => v + 1);
    drain(); // release messages now that these authors are decorated
    // Drain the rest.
    if (pendingRef.current.size > 0 && flushTimerRef.current == null) {
      flushTimerRef.current = setTimeout(flush, 300);
    }
  }, [drain]);

  const queueResolve = useCallback(
    (key: string) => {
      if (!key || pendingRef.current.has(key)) return;
      // A known chatter keeps showing what they had while a stale answer is
      // asked again; their messages are never held for it.
      const at = resolvedAtRef.current.get(key);
      if (cosmeticsRef.current.has(key) && at !== undefined && Date.now() - at < COSMETICS_TTL_MS) return;
      // A chatter seen in an earlier session shows their last look at once (no
      // hold); the fresh answer below replaces it, changing the row only if
      // they switched cosmetics since.
      const look = lookStandIn(key, cosmeticsRef.current.has(key));
      if (look) {
        const identity = { ...emptyIdentity(key), ...look } as ResolvedIdentity;
        cosmeticsRef.current.set(key, identity);
        installPersonal(key, identity);
        setCosmeticsVersion((v) => v + 1);
      }
      pendingRef.current.add(key);
      if (flushTimerRef.current == null) flushTimerRef.current = setTimeout(flush, FIRST_ASK_MS);
    },
    [flush],
  );

  // Ask a chatter's cosmetics again now, because an event says they changed.
  // Only chatters this overlay has asked about; they keep their current look
  // until the answer lands.
  const refreshChatter = useCallback(
    (key: string) => {
      if (pendingRef.current.has(key) || !latestAskRef.current.has(key)) return;
      pendingRef.current.add(key);
      if (flushTimerRef.current == null) flushTimerRef.current = setTimeout(flush, 250);
    },
    [flush],
  );

  // Change events. StreamNook's are keyed by the member's Twitch id, which is
  // the chat key on Twitch and the identity's `twitch_user_id` for a Kick or
  // YouTube account the member claimed. 7TV's are keyed by Twitch id.
  useEffect(() => {
    const stopMembers = watchMemberCosmetics((change) => {
      for (const [key, identity] of cosmeticsRef.current) {
        const hit =
          'memberId' in change
            ? key === change.memberId || identity.twitch_user_id === change.memberId
            : identity.sn_number != null;
        if (hit) refreshChatter(key);
      }
    });
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const stopSevenTv = onSevenTvCosmeticChange((twitchUserId) => {
      const t = setTimeout(() => {
        timers.delete(t);
        refreshChatter(twitchUserId);
      }, SEVENTV_SETTLE_MS);
      timers.add(t);
    });
    return () => {
      stopMembers();
      stopSevenTv();
      for (const t of timers) clearTimeout(t);
    };
  }, [refreshChatter]);

  const loadRoomRoleArt = useCallback((roomId: string) => {
    if (roleArtRef.current.has(roomId)) return;
    void loadRoleArt(roomId).then((art) => {
      if (!art) return;
      roleArtRef.current.set(roomId, art);
      setCosmeticsVersion((v) => v + 1);
    });
  }, []);

  // Turning custom role art on reads the rooms already seen.
  useEffect(() => {
    customRoleArtRef.current = customRoleArt;
    if (customRoleArt) for (const roomId of loadedRoomsRef.current) loadRoomRoleArt(roomId);
  }, [customRoleArt, loadRoomRoleArt]);

  const pushMessage = useCallback(
    (msg: OverlayBaseMessage) => {
      // Drop replayed ids (see seenIdsRef). Bounded so a long stream can't grow
      // the set forever: past 2000 ids, keep the newest 1000 (insertion order).
      if (msg.id) {
        const seen = seenIdsRef.current;
        if (seen.has(msg.id)) return;
        seen.add(msg.id);
        if (seen.size > 2000) seenIdsRef.current = new Set(Array.from(seen).slice(-1000));
      }
      // Enqueue rather than show immediately; the message releases once its author's
      // cosmetics resolve (see drain), so it appears fully decorated.
      holdQueueRef.current.push({ msg, ts: Date.now() });
      const key = cosmeticsKeyOf(msg);
      if (key) queueResolve(key);
      // Load the channel's Twitch badge images once its room id first appears,
      // and a shared-chat row's own channel's, whose badges it wears.
      const roomId = msg.tags?.['room-id'];
      if (roomId && customRoleArtRef.current) loadRoomRoleArt(roomId);
      for (const room of badgeRoomsFor(msg.tags)) {
        if (loadedRoomsRef.current.has(room)) continue;
        loadedRoomsRef.current.add(room);
        fetch(`/api/twitch/badges?room=${encodeURIComponent(room)}`)
          .then((r) => (r.ok ? (r.json() as Promise<{ badges?: Record<string, string>; titles?: Record<string, string> }>) : null))
          .then((d) => {
            if (!d?.badges) return;
            storeRoomBadges(twitchBadgesRef.current, room, d.badges, d.titles);
            setCosmeticsVersion((v) => v + 1);
          })
          .catch(() => {});
      }
      drain(); // release anything already ready (e.g. a cached author) immediately
    },
    [queueResolve, drain, loadRoomRoleArt],
  );

  // A change to a Twitch channel's 7TV emote set, as one overlay row each.
  const pushEmoteSetChanges = useCallback(
    (channel: string, changes: SeventvEmoteChange[]) => {
      const now = Date.now();
      changes.forEach((change, i) =>
        pushMessage({
          id: `7tv-${channel}-${now}-${i}-${Math.random().toString(36).slice(2, 8)}`,
          user_id: '',
          username: '',
          display_name: '',
          badges: [],
          timestamp: new Date(now).toISOString(),
          content: '',
          provider: 'twitch',
          channel,
          seventvEmoteChange: change,
        }),
      );
    },
    [pushMessage],
  );

  // Rows whose emotes were mended (a table that landed late, a live 7TV edit, a
  // personal set; see emoteRepair), patched in place by id, held ones included.
  // A row that was retracted or has gone stays as it is.
  const applySegmentUpdates = useCallback((updates: SegmentUpdate[]) => {
    const byId = new Map(updates.map((u) => [u.id, u.segments]));
    for (const h of holdQueueRef.current) {
      const segments = byId.get(h.msg.id);
      if (segments) h.msg = { ...h.msg, segments };
    }
    setMessages((prev) => {
      let any = false;
      const next = prev.map((m) => {
        const segments = byId.get(m.id);
        if (!segments || m.retracted) return m;
        any = true;
        return { ...m, segments };
      });
      return any ? next : prev;
    });
  }, []);

  // Moderation: retract messages — both already-shown and still-held. Shown
  // messages aren't dropped instantly (a jarring mid-screen layout snap):
  // they're marked `retracted` so the renderer fades them out, then removed for
  // real a beat later. Held (not yet shown) messages just get dropped. The ids
  // to remove are captured at retract time, so a message that arrives during
  // the fade window is never swept up by the pending removal.
  const RETRACT_MS = 300;
  const retract = useCallback((match: (m: OverlayBaseMessage) => boolean) => {
    holdQueueRef.current = holdQueueRef.current.filter((h) => !match(h.msg));
    const ids = new Set<string>();
    setMessages((p) => {
      let any = false;
      const next = p.map((m) => {
        if (!match(m) || m.retracted) return m;
        any = true;
        ids.add(m.id);
        return { ...m, retracted: true };
      });
      return any ? next : p;
    });
    setTimeout(() => {
      if (ids.size > 0) setMessages((p) => p.filter((m) => !ids.has(m.id)));
    }, RETRACT_MS);
  }, []);
  // Message ids are globally unique enough to match alone; user ids are only
  // unique per platform (Twitch and Kick both use bare numbers), so user-level
  // and channel-level retractions are always provider/channel scoped.
  const removeById = useCallback((id: string) => retract((m) => m.id === id), [retract]);
  const removeByUser = useCallback(
    (uid: string, provider: string) => retract((m) => m.user_id === uid && (m.provider ?? 'twitch') === provider),
    [retract],
  );
  const clearChannel = useCallback(
    (provider: string, channel: string) =>
      retract(
        (m) =>
          (m.provider ?? 'twitch') === provider &&
          (!channel || (m.channel ?? '').toLowerCase() === channel.toLowerCase()),
      ),
    [retract],
  );

  // A moderator's !clearoverlay or !refreshoverlay (Twitch only; the client has
  // already checked who sent it and that it is live chat, not history).
  const runCommand = useCallback(
    (cmd: OverlayCommand) => {
      if (cmd === 'clear') {
        holdQueueRef.current = [];
        setMessages([]);
        try {
          localStorage.removeItem(`sn_overlay_buf_v1:${overlayId}`);
        } catch {
          /* ignore */
        }
        return;
      }
      // One reload per few seconds, however many times the command is typed.
      try {
        const last = Number(sessionStorage.getItem(REFRESH_STAMP_KEY) || 0);
        if (Date.now() - last < REFRESH_COOLDOWN_MS) return;
        sessionStorage.setItem(REFRESH_STAMP_KEY, String(Date.now()));
      } catch {
        /* no storage: reload anyway */
      }
      window.location.reload();
    },
    [overlayId],
  );

  // Chat connects once; these let it see the current setting and handler
  // without reconnecting when a published style change arrives.
  const loadRecentChat = style.loadRecentChat === true;
  const modCommandsRef = useRef(style.modCommands !== false);
  const runCommandRef = useRef(runCommand);
  useEffect(() => {
    modCommandsRef.current = style.modCommands !== false;
    runCommandRef.current = runCommand;
  });

  // Warm this overlay's channel emotes into StreamNook's image store, so they
  // still draw when 7TV, BTTV or FFZ is down.
  useEffect(() => startEmotePrefill(overlayId), [overlayId]);

  // drain wakes itself when its next row is due; this slow tick is only a safety
  // net (a timer the browser dropped), and unmounting stops the wake-up timer.
  useEffect(() => {
    const t = setInterval(drain, 1_000);
    return () => {
      clearInterval(t);
      if (drainTimerRef.current) clearTimeout(drainTimerRef.current);
    };
  }, [drain]);

  // ── Buffer persistence: an OBS/browser reload keeps the last visible chats ──
  // The overlay is a live feed with no history source, so without this a browser
  // source reload (OBS restart, stream PC reboot) comes back blank. The buffer is
  // saved per overlay id and restored through pushMessage, so restored chats get
  // their cosmetics back too. Skipped for the ?live= diagnostic mode ('demo')
  // and the builder preview (`persist` false).
  const storageKey = `sn_overlay_buf_v1:${overlayId}`;
  const keepsBuffer = persist && overlayId !== 'demo';
  const restoreRanRef = useRef(false);
  const hadContentRef = useRef(false);
  useEffect(() => {
    if (restoreRanRef.current || !keepsBuffer) return;
    restoreRanRef.current = true;
    // Off clears on reload, except a reload in the middle of a stream (the page
    // was alive moments ago: an OBS refresh, a scene switch): chat comes back
    // then too (reloadRestore). Read before this page's own heartbeat writes.
    let aliveAt = 0;
    try {
      aliveAt = Number(localStorage.getItem(aliveKey(overlayId)) || 0);
    } catch {
      /* no storage: treat as a fresh start */
    }
    if (!shouldRestore(style.restoreOnReload === true, aliveAt, Date.now())) {
      try {
        localStorage.removeItem(storageKey);
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw) as OverlayBaseMessage[];
      // Restored rows are catch-up: they appear without an entrance.
      if (Array.isArray(saved)) {
        for (const m of saved.slice(-RESTORE_MAX)) pushMessage({ ...m, metadata: { ...m.metadata, from_backfill: true } });
      }
    } catch {
      /* unavailable or corrupt storage — start empty */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, pushMessage]);
  // The heartbeat a reload checks (after the restore above has read it).
  useEffect(() => {
    if (!keepsBuffer) return;
    const beat = () => {
      try {
        localStorage.setItem(aliveKey(overlayId), String(Date.now()));
      } catch {
        /* no storage: a reload just starts clear */
      }
    };
    beat();
    const t = setInterval(beat, HEARTBEAT_MS);
    return () => clearInterval(t);
  }, [overlayId, keepsBuffer]);
  useEffect(() => {
    if (!keepsBuffer) return;
    // Never write before the restore has drained into state, or the initial empty
    // render would clobber the saved buffer. After content has shown once, empty
    // IS meaningful (a moderation clear) and persists.
    if (messages.length > 0) hadContentRef.current = true;
    else if (!hadContentRef.current) return;
    try {
      // Mid-fade deletions never persist — a restored buffer must not resurrect
      // a moderated message (its removal timer wouldn't survive the reload).
      localStorage.setItem(storageKey, JSON.stringify(messages.filter((m) => !m.retracted).slice(-RESTORE_MAX)));
    } catch {
      /* storage full/unavailable — overlay still works, just won't restore */
    }
  }, [messages, storageKey, keepsBuffer]);

  const twitchKey = channels.filter((c) => c.provider === 'twitch').map((c) => c.channel.toLowerCase()).sort().join(',');
  const otherKey = channels
    .filter((c) => c.provider !== 'twitch')
    .map((c) => `${c.provider}:${c.channel}`)
    .sort()
    .join(',');

  // A channel taken out of the overlay takes its rows with it (channelRows).
  const channelSetRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    const now = new Set(channels.map((c) => channelRowKey(c.provider, c.channel)));
    const before = channelSetRef.current;
    channelSetRef.current = now;
    if (!before) return;
    const gone = goneChannels(before, now);
    if (gone.size === 0) return;
    const fromGone = (m: OverlayBaseMessage) => isFromGoneChannel(m, gone);
    holdQueueRef.current = holdQueueRef.current.filter((h) => !fromGone(h.msg));
    setMessages((prev) => (prev.some(fromGone) ? prev.filter((m) => !fromGone(m)) : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twitchKey, otherKey]);

  // ── Twitch: straight from the browser, no server ──────────────────────────
  useEffect(() => {
    const twitchChannels = channels.filter((c) => c.provider === 'twitch');
    if (twitchChannels.length === 0) return;
    const disposers = twitchChannels.map((c) =>
      connectTwitch(
        c.channel,
        {
          onMessage: pushMessage,
          onDelete: removeById,
          onClearUser: (uid) => removeByUser(uid, 'twitch'),
          onClearChat: () => clearChannel('twitch', c.channel),
          onCommand: (cmd) => runCommandRef.current(cmd),
          onEmoteSetChange: (changes) => pushEmoteSetChanges(c.channel, changes),
          onUpdate: applySegmentUpdates,
        },
        { history: loadRecentChat, commands: () => modCommandsRef.current },
      ),
    );
    return () => disposers.forEach((d) => d());
    // History is a start-up fill: turning it on later does not reconnect chat.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twitchKey, pushMessage, pushEmoteSetChanges, removeById, removeByUser, clearChannel, applySegmentUpdates]);

  // ── Kick / YouTube / TikTok: via the homelab ingestion service ────────────
  const relayQueryRef = useRef(relayQuery);
  useEffect(() => {
    relayQueryRef.current = relayQuery;
  });
  useEffect(() => {
    const others = channels.filter((c) => c.provider !== 'twitch');
    if (others.length === 0) return;
    let ws: WebSocket | null = null;
    let closed = false;
    let retry = 1000;
    let settled: ReturnType<typeof setTimeout> | undefined;
    const deliver = (raw: object) => {
      // Moderation frames from the service (Kick/YouTube/TikTok deletions):
      // retract exactly like a Twitch CLEARMSG/CLEARCHAT does.
      if ((raw as { type?: unknown }).type === 'mod') {
        const f = raw as { action?: string; target?: string; provider?: string; channel?: string };
        if (f.action === 'delete_msg' && f.target) removeById(f.target);
        else if (f.action === 'delete_user' && f.target && f.provider) removeByUser(f.target, f.provider);
        else if (f.action === 'clear' && f.provider) clearChannel(f.provider, f.channel ?? '');
        return;
      }
      const m = raw as OverlayBaseMessage;
      // The service may also carry Twitch; the browser already handles that, so
      // only take what the browser can't reach.
      if ((m.provider ?? 'twitch') !== 'twitch') pushMessage(m);
    };
    // Links and the channel's 7TV emotes, as the app adds them (relayRows).
    const relayRows = createRelayRows({ onFrame: deliver, onUpdate: applySegmentUpdates });
    // Where this page is in each relay channel's stream; a reconnect asks for
    // exactly what came after (relayCursor).
    const cursors = new Map<string, string>();
    const retryLater = () => {
      retry = Math.min(retry * 2, 15000);
      setTimeout(() => void connect(), retry);
    };
    const connect = async () => {
      // A retry timer can fire after the feed has gone.
      if (closed) return;
      let query = `id=${encodeURIComponent(overlayId)}`;
      const ask = relayQueryRef.current;
      if (ask) {
        const asked = await ask().catch(() => null);
        if (closed) return;
        if (!asked) {
          retryLater();
          return;
        }
        query = asked;
      }
      query += sinceParam(cursors);
      ws = new WebSocket(`${OVERLAY_WS}?${query}`);
      // The backoff resets on a frame or after 10 s open, never on open alone:
      // the relay can take a socket and close it straight away (an unknown id),
      // and resetting on open would retry that every 2 s forever. The 10 s rule
      // covers a quiet channel, which sends no frames (the relay's pings never
      // reach the page) but whose drops should still reconnect quickly.
      ws.onopen = () => {
        settled = setTimeout(() => {
          retry = 1000;
        }, 10_000);
      };
      ws.onmessage = (ev) => {
        retry = 1000;
        let raw: unknown;
        try {
          raw = JSON.parse(ev.data as string);
        } catch {
          return; /* ignore malformed frame */
        }
        if (raw && typeof raw === 'object') {
          noteFrame(cursors, raw);
          relayRows.handle(raw);
        }
      };
      ws.onclose = () => {
        clearTimeout(settled);
        if (closed) return;
        retryLater();
      };
      ws.onerror = () => ws?.close();
    };
    void connect();
    return () => {
      closed = true;
      clearTimeout(settled);
      ws?.close();
      relayRows.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayId, otherKey, pushMessage, removeById, removeByUser, clearChannel, applySegmentUpdates]);

  const adapted = useMemo<OverlayMessage[]>(() => {
    void cosmeticsVersion;
    const roleArt = customRoleArt ? roleArtRef.current : null;
    return messages.map((m) => adaptMessage(m, cosmeticsRef.current, twitchBadgesRef.current, roleArt));
  }, [messages, cosmeticsVersion, customRoleArt]);

  // Render at the CONFIGURED canvas size (not the full viewport), so the overlay
  // respects the width/height the streamer set — the browser source / page can be
  // any size and the chat stays within its bounds (matches the builder preview).
  return (
    <div style={{ width: `${style.width}px`, height: `${style.height}px`, position: 'relative', overflow: 'hidden' }}>
      {/* Supersample 2× so text stays crisp in OBS (its browser renders at dpr 1). */}
      <OverlayChat messages={adapted} style={style} superSample={2} />
      {showStatus && messages.length === 0 && <div style={STATUS_STYLE}>Waiting for chat…</div>}
    </div>
  );
}

const STATUS_STYLE: CSSProperties = {
  position: 'absolute',
  left: 12,
  bottom: 10,
  fontSize: 12,
  color: 'rgba(255,255,255,0.7)',
  textShadow: '0 1px 2px rgba(0,0,0,0.85)',
  pointerEvents: 'none',
};
